/**
 * Talks to the Octbill API. Handles the CSRF token for writes and turns problem+json
 * responses into ApiError, with the server's message, code and field errors.
 */
let csrfToken = null;

export class ApiError extends Error {
  constructor(status, body = {}) {
    super(body.detail ?? `Request failed (HTTP ${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code ?? null;
    this.errors = body.errors ?? [];
    this.details = body.details ?? null;
  }
  /** Message for one form field, from a validation.failed response. */
  fieldError(field) {
    return this.errors.find((e) => e.field === `body.${field}` || e.field.endsWith(`.${field}`))?.message;
  }
}

export function setCsrfToken(token) {
  csrfToken = token;
}

// Logging out: reads still in flight are aborted and new ones answered here with a 401, so pollers
// (live stats, consoles) don't reach the API after the session is gone. Who's logged in is still
// asked (bootstrap), and a session that starts again lifts this.
const SESSION_FREE = /^\/api\/(auth\/|public\/)/;
let signedOut = false;
const reads = new Set();
const loggedOutError = () => new ApiError(401, { code: 'auth.unauthenticated', detail: 'You are logged out.' });

export function endSession() {
  signedOut = true;
  for (const controller of reads) controller.abort();
}

export function startSession() {
  signedOut = false;
}

async function ensureCsrf(fetchImpl) {
  if (!csrfToken) {
    const res = await fetchImpl('/api/auth/csrf', {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
    csrfToken = (await res.json()).csrf_token;
  }
  return csrfToken;
}

/**
 * api('/api/client/account') · api(path, { method: 'POST', body }) · api(path, { raw: true }) for
 * non-JSON downloads · api(path, { method: 'POST', text }) to send plain text (the file editor) ·
 * api(path, { method: 'POST', form }) to upload a FormData (the browser sets the multipart boundary) ·
 * `headers` adds request headers (e.g. cf-turnstile-response on a GET).
 * Returns parsed JSON (or text/Blob), or null for 204.
 */
export async function api(
  path,
  { method = 'GET', body, text, form, headers: extra, raw = false, fetchImpl = fetch, retried = false } = {},
) {
  const headers = { accept: 'application/json', ...extra };
  if (method !== 'GET') headers['x-csrf-token'] = await ensureCsrf(fetchImpl);
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (text !== undefined) headers['content-type'] = 'text/plain; charset=utf-8';

  const read = method === 'GET' && !SESSION_FREE.test(path);
  if (read && signedOut) throw loggedOutError();
  const controller = read ? new AbortController() : null;
  if (controller) reads.add(controller);
  let res;
  try {
    res = await fetchImpl(path, {
      method,
      headers,
      credentials: 'same-origin',
      ...(controller ? { signal: controller.signal } : {}),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(text !== undefined ? { body: text } : {}),
      ...(form !== undefined ? { body: form } : {}),
    });
  } catch (err) {
    if (controller?.signal.aborted && signedOut) throw loggedOutError();
    throw err;
  } finally {
    if (controller) reads.delete(controller);
  }

  if (res.status === 204) return null;
  const type = res.headers.get('content-type') ?? '';
  if (res.ok && raw) return type.includes('json') ? res.text() : res.blob();
  const data = type.includes('json') ? await res.json() : { detail: await res.text() };

  if (!res.ok) {
    // The session (and its CSRF token) may have rotated: fetch a fresh token and retry once.
    if (res.status === 403 && data.code === 'auth.csrf_invalid' && !retried) {
      csrfToken = null;
      return api(path, { method, body, text, form, headers: extra, raw, fetchImpl, retried: true });
    }
    throw new ApiError(res.status, data);
  }
  if (data && typeof data === 'object' && data.csrf_token) csrfToken = data.csrf_token;
  return data;
}

/** Downloads an API response as a file in the browser. */
export async function download(path, filename) {
  const blob = await api(path, { raw: true });
  const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob], { type: 'application/json' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
